import {
  ClientGameState,
  RoomJoinedMessage,
  PlayerJoinedMessage,
  PlayerLeftMessage,
  PlayerPosition,
  GameOverMessage,
} from '@hokm/shared';
import { NetworkManager } from '../../../managers/NetworkManager';

export interface LobbyNetworkCallbacks {
  onRoomJoined: (data: RoomJoinedMessage) => void;
  onPlayerJoined: (displayPosition: PlayerPosition, data: PlayerJoinedMessage) => void;
  onPlayerLeft: (displayPosition: PlayerPosition) => void;
  onGameStarted: (state: ClientGameState) => void;
  onGameOver: (state: ClientGameState, data: GameOverMessage) => void;
  onLobbyState: (state: ClientGameState) => void;
  onError: (message: string) => void;
  onKicked: (reason: string) => void;
  toDisplayPosition: (serverPosition: PlayerPosition) => PlayerPosition;
}

/**
 * Handles network events for the lobby scene.
 */
export class LobbyNetworkHandler {
  private pendingGameEndState: ClientGameState | null = null;

  constructor(
    private networkManager: NetworkManager,
    private callbacks: LobbyNetworkCallbacks
  ) {
    this.setupListeners();
  }

  private setupListeners(): void {
    this.networkManager.on('roomJoined', (data: RoomJoinedMessage) => {
      this.callbacks.onRoomJoined(data);
    });

    this.networkManager.on('playerJoined', (data: PlayerJoinedMessage) => {
      const displayPos = this.callbacks.toDisplayPosition(data.player.position);
      this.callbacks.onPlayerJoined(displayPos, data);
    });

    this.networkManager.on('playerLeft', (data: PlayerLeftMessage) => {
      const displayPos = this.callbacks.toDisplayPosition(data.position);
      this.callbacks.onPlayerLeft(displayPos);
    });

    this.networkManager.on('gameState', (state: ClientGameState) => {
      if (state.phase === 'game_end') {
        // Game has ended - wait for GAME_OVER message before transitioning
        this.pendingGameEndState = state;
      } else if (state.phase !== 'lobby') {
        this.callbacks.onGameStarted(state);
      } else {
        this.callbacks.onLobbyState(state);
      }
    });

    this.networkManager.on('gameOver', (data: GameOverMessage) => {
      if (this.pendingGameEndState) {
        this.callbacks.onGameOver(this.pendingGameEndState, data);
        this.pendingGameEndState = null;
      }
    });

    this.networkManager.on('error', (data: { code: string; message: string }) => {
      console.error('Server error:', data.code, data.message);
      this.callbacks.onError(data.message);
    });

    this.networkManager.on('kicked', (data: { reason: string }) => {
      this.callbacks.onKicked(data.reason);
    });
  }

  cleanup(): void {
    this.networkManager.off('roomJoined');
    this.networkManager.off('playerJoined');
    this.networkManager.off('playerLeft');
    this.networkManager.off('gameState');
    this.networkManager.off('gameOver');
    this.networkManager.off('error');
    this.networkManager.off('kicked');
  }
}
